import { configureStore, combineReducers, createDynamicMiddleware } from '@reduxjs/toolkit'
import type { Middleware, Reducer, UnknownAction } from '@reduxjs/toolkit'
import authReducer from './slices/authSlice'
import { projectDataApi } from './services/projectDataApi'
import { taskActionsApi } from './services/taskActionsApi'

const staticReducers = {
  auth: authReducer,
  [projectDataApi.reducerPath]: projectDataApi.reducer,
  [taskActionsApi.reducerPath]: taskActionsApi.reducer,
}

const dynamicReducers: Record<string, Reducer<unknown, UnknownAction>> = {}

function createRootReducer() {
  return combineReducers({ ...staticReducers, ...dynamicReducers })
}

// Admin API middleware (services/adminApiBases.ts) joins the chain only when an admin route loads.
const adminMiddleware = createDynamicMiddleware()

export const store = configureStore({
  reducer: createRootReducer(),
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(
    projectDataApi.middleware,
    taskActionsApi.middleware,
    adminMiddleware.middleware,
  ),
})

interface RegistrableApi {
  reducerPath: string
  // RTK Query shell reducers each own an opaque state slice; under strictFunctionTypes a
  // Reducer<SpecificState> is not a Reducer<unknown> (contravariant state parameter), so
  // accept redux's default any-state Reducer, the way combineReducers inputs are typed.
  reducer: Reducer
  middleware: Middleware
}

// Registers lazily-created APIs' reducers and middleware with the store. Called from
// services/adminApiBases.ts when the first admin route chunk loads, so the admin-only RTK Query
// slices are absent from startup state and middleware registration for visitors who never open
// the admin dashboard.
export function registerAdminApis(apis: readonly RegistrableApi[]): void {
  let changed = false
  for (const api of apis) {
    if (dynamicReducers[api.reducerPath]) continue
    dynamicReducers[api.reducerPath] = api.reducer
    adminMiddleware.addMiddleware(api.middleware)
    changed = true
  }
  if (changed) store.replaceReducer(createRootReducer())
}

export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch
